import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import useScrollTo from '../hooks/useScrollTo';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import {
  FaCode,
  FaMobileAlt,
  FaServer,
  FaRocket,
  FaSearchPlus,
  FaEnvelopeOpenText,
  FaStar,
  FaBell,
  FaChartLine,
  FaMousePointer,
  FaChartPie,
  FaFileInvoiceDollar,
  FaHeadset,
  FaTools,
  FaUsers,
  FaCloud,
  FaShieldAlt,
  FaLaptopCode,
} from 'react-icons/fa';

const Services = () => {
  const { scrollToSection } = useScrollTo();
  const serviceCategories = [
    {
      category: 'Web Design & Development',
      description: 'Enterprise-grade digital solutions engineered for performance and scalability',
      gradient: 'from-blue-600 via-indigo-600 to-purple-600',
      services: [
        {
          icon: <FaCode className="text-4xl" />,
          title: 'Custom Website Design',
          description: 'Strategic, brand-aligned designs that convert visitors into customers while delivering exceptional user experiences across all platforms and devices.',
          iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
        },
        {
          icon: <FaMobileAlt className="text-4xl" />,
          title: 'Responsive Development',
          description: 'Mobile-first architecture ensuring flawless performance and pixel-perfect presentation across smartphones, tablets, and desktop environments.',
          iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-600',
        },
        {
          icon: <FaServer className="text-4xl" />,
          title: 'Custom Web-Based Software Application Development',
          description: 'Scalable enterprise applications built with modern frameworks, featuring secure backends, intuitive interfaces, and seamless third-party integrations.',
          iconBg: 'bg-gradient-to-br from-purple-500 to-pink-600',
        },
        {
          icon: <FaRocket className="text-4xl" />,
          title: 'Website Redesign & Performance Optimization',
          description: 'Modernization and optimization services that dramatically improve site speed, user engagement, conversion rates, and overall digital presence.',
          iconBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
        },
        {
          icon: <FaSearchPlus className="text-4xl" />,
          title: 'SEO Strategy & Implementation',
          description: 'Data-driven SEO solutions designed to improve search rankings, drive qualified organic traffic, and increase your market visibility.',
          iconBg: 'bg-gradient-to-br from-green-500 to-teal-600',
        },
      ],
    },
    {
      category: 'Business Automation',
      description: 'Intelligent workflow automation reducing costs and accelerating growth',
      gradient: 'from-orange-500 via-amber-500 to-yellow-500',
      services: [
        {
          icon: <FaEnvelopeOpenText className="text-4xl" />,
          title: 'Email Marketing Automation',
          description: 'Enterprise-level email automation systems featuring personalized campaigns, behavioral triggers, and advanced segmentation for maximum engagement and ROI.',
          iconBg: 'bg-gradient-to-br from-orange-500 to-amber-600',
        },
        {
          icon: <FaStar className="text-4xl" />,
          title: 'Reputation Management Automation',
          description: 'Automated reputation management systems that systematically generate customer reviews, enhance online credibility, and strengthen local SEO performance.',
          iconBg: 'bg-gradient-to-br from-yellow-500 to-orange-600',
        },
        {
          icon: <FaBell className="text-4xl" />,
          title: 'Financial Process Automation',
          description: 'Automated billing and payment reminder systems that accelerate receivables, improve cash flow predictability, and reduce administrative overhead.',
          iconBg: 'bg-gradient-to-br from-red-500 to-rose-600',
        },
      ],
    },
    {
      category: 'Analytics & Business Intelligence',
      description: 'Advanced analytics transforming data into strategic competitive advantages',
      gradient: 'from-green-600 via-teal-600 to-cyan-600',
      services: [
        {
          icon: <FaChartLine className="text-4xl" />,
          title: 'Real-Time Business Analytics',
          description: 'Enterprise dashboards providing instant visibility into KPIs, user behavior, and operational metrics enabling data-driven decision making at every level.',
          iconBg: 'bg-gradient-to-br from-green-500 to-teal-600',
        },
        {
          icon: <FaMousePointer className="text-4xl" />,
          title: 'Engagement Analytics',
          description: 'Sophisticated tracking and analysis of user interactions across digital channels, providing actionable insights to optimize conversion and retention strategies.',
          iconBg: 'bg-gradient-to-br from-teal-500 to-cyan-600',
        },
        {
          icon: <FaChartPie className="text-4xl" />,
          title: 'Customer Intelligence',
          description: 'Comprehensive customer analytics revealing lifetime value, behavioral patterns, and engagement trends to drive personalization and revenue growth.',
          iconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
        },
        {
          icon: <FaFileInvoiceDollar className="text-4xl" />,
          title: 'Financial Analytics & Reporting',
          description: 'Executive-level financial intelligence with customizable reporting, profitability analysis, and forecasting tools for strategic business planning.',
          iconBg: 'bg-gradient-to-br from-emerald-500 to-green-600',
        },
      ],
    },
    {
      category: 'Managed IT Services & Support',
      description: 'Proactive IT management ensuring security, reliability, and peak performance',
      gradient: 'from-purple-600 via-pink-600 to-rose-600',
      services: [
        {
          icon: <FaHeadset className="text-4xl" />,
          title: 'Enterprise Technical Support',
          description: 'Dedicated support team providing rapid issue resolution, proactive monitoring, and expert guidance to minimize downtime and maximize productivity.',
          iconBg: 'bg-gradient-to-br from-purple-500 to-pink-600',
        },
        {
          icon: <FaTools className="text-4xl" />,
          title: 'Website Maintenance & Enhancement',
          description: 'Ongoing maintenance programs including security updates, performance optimization, feature enhancements, and technology stack modernization.',
          iconBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
        },
        {
          icon: <FaUsers className="text-4xl" />,
          title: 'CRM & Client Management Solutions',
          description: 'Custom CRM implementations and integrations enabling streamlined client relationships, automated workflows, and data-driven customer engagement.',
          iconBg: 'bg-gradient-to-br from-rose-500 to-red-600',
        },
        {
          icon: <FaCloud className="text-4xl" />,
          title: 'Infrastructure & Hosting Services',
          description: 'End-to-end infrastructure management including cloud hosting, domain administration, enterprise email, and scalable server solutions.',
          iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
        },
        {
          icon: <FaShieldAlt className="text-4xl" />,
          title: 'Cybersecurity & Data Protection',
          description: 'Multi-layered security architecture featuring threat monitoring, automated backups, disaster recovery planning, and compliance management.',
          iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-600',
        },
        {
          icon: <FaLaptopCode className="text-4xl" />,
          title: 'IT Strategy & Consulting',
          description: 'Strategic technology consulting aligning IT infrastructure with business objectives, optimizing costs, and maximizing operational efficiency.',
          iconBg: 'bg-gradient-to-br from-teal-500 to-cyan-600',
        },
      ],
    },
  ];

  const scrollToConsultation = () => scrollToSection('consultation');

  const ServiceCard = ({ service }) => (
    <div className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden h-full border border-gray-100 dark:border-gray-700">
      <div className="relative z-10 p-6 h-full flex flex-col">
        <div className={`inline-flex p-3 rounded-lg ${service.iconBg} text-white mb-4 group-hover:scale-110 transition-all duration-300 shadow-md self-start`}>
          {service.icon}
        </div>
        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-teal dark:group-hover:from-teal dark:group-hover:to-primary transition-all duration-300">
          {service.title}
        </h4>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm flex-grow">
          {service.description}
        </p>
      </div>
      <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary/5 to-teal/5 dark:from-teal/10 dark:to-primary/10 rounded-full filter blur-xl group-hover:scale-150 transition-transform duration-500"></div>
    </div>
  );

  const CategorySection = ({ category, index }) => (
    <div className="mb-16 md:mb-20 animate-fade-in-up" style={{ animationDelay: `${index * 0.2}s` }}>
      {/* Category Header */}
      <div className="mb-8 text-center">
        <div className="inline-block">
          <h3 className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-3 bg-gradient-to-r ${category.gradient} bg-clip-text text-transparent`}>
            {category.category}
          </h3>
          <div className={`h-1 bg-gradient-to-r ${category.gradient} rounded-full mb-3`}></div>
        </div>
        <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {category.description}
        </p>
      </div>

      {/* Services Swiper - Mobile Only */}
      <div className="block md:hidden">
        <Swiper
          modules={[Pagination, Autoplay]}
          grabCursor={true}
          centeredSlides={true}
          slidesPerView={1}
          spaceBetween={20}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          className="services-category-swiper pb-12"
        >
          {category.services.map((service, idx) => (
            <SwiperSlide key={idx}>
              <ServiceCard service={service} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Services Grid - Tablet & Desktop */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {category.services.map((service, idx) => (
          <div
            key={idx}
            className="animate-fade-in-up"
            style={{ animationDelay: `${(index * 0.2) + (idx * 0.1)}s` }}
          >
            <ServiceCard service={service} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="services" className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/4 w-96 h-96 bg-accent/5 dark:bg-accent/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20 animate-fade-in-up">
          <div className="inline-block">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-gray-900 dark:text-white">Our </span>
              <span className="gradient-text">Services</span>
            </h2>
            <div className="h-1.5 bg-gradient-to-r from-primary via-teal to-accent rounded-full"></div>
          </div>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto mt-6 leading-relaxed">
            Enterprise-grade digital solutions designed to accelerate growth, optimize operations, and drive measurable business outcomes. Partner with a team that understands your challenges and delivers results.
          </p>
        </div>

        {/* Service Categories */}
        {serviceCategories.map((category, index) => (
          <CategorySection key={index} category={category} index={index} />
        ))}

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-primary/10 via-teal/10 to-accent/10 dark:from-primary/20 dark:via-teal/20 dark:to-accent/20 p-8 md:p-12 rounded-3xl backdrop-blur-sm animate-fade-in-up border border-gray-200 dark:border-gray-700">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Accelerate Your Digital Transformation?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Partner with CitiTekPro to build scalable solutions that drive growth, improve efficiency, and deliver competitive advantages in today's digital economy
          </p>
          <button
            onClick={scrollToConsultation}
            className="group relative px-8 py-4 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Free Consultation
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
            <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
